/**
 * Main App Component
 * Manages routing and global layout
 * Uses keep-alive pattern for TrainingView to prevent PDF reload on tab switch
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { BOMProvider } from './contexts/BOMContext';
import { ThemeContextProvider, useAppTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import Layout from './components/Layout';
import UploadView from './components/UploadView';
import KnownView from './components/KnownView';
import PartsView from './components/PartsView';
import ExtractionHistory from './components/ExtractionHistory';
import LoadingOverlay from './components/LoadingOverlay';
import ToastContainer from './components/ToastContainer';
import { TrainingView } from './components/training';
import LoginPage from './components/LoginPage';
import TeamsView from './components/TeamsView';
import SettingsPage from './components/SettingsPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import AdminView from './components/AdminView';
import BOMView from './components/BOMView';
import ReconciliationView from './components/ReconciliationView';
import { EstimatorView } from './components/phase2';
import SchematicPopout from './components/phase2/SchematicPopout';

// Keep-alive wrapper - keeps component mounted but hidden
const KeepAlive: React.FC<{ visible: boolean; children: React.ReactNode }> = ({ visible, children }) => (
  <Box sx={{ display: visible ? 'block' : 'none', height: visible ? 'auto' : 0, overflow: 'hidden' }}>
    {children}
  </Box>
);

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><LoadingOverlay /></Box>;
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Main content with keep-alive for Training view (prevents PDF reload on tab switch)
const MainContent: React.FC = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  return (
    <>
      {/* Training view is always mounted but hidden when not active - prevents PDF reload */}
      <KeepAlive visible={currentPath === '/training' || currentPath === '/'}>
        <TrainingView />
      </KeepAlive>
      
      {/* Other views use normal routing (mount/unmount) */}
      <Routes>
        <Route path="/" element={null} /> {/* Handled by keep-alive above */}
        <Route path="/training" element={null} /> {/* Handled by keep-alive above */}
        <Route path="/upload" element={<UploadView />} />
        <Route path="/known" element={<KnownView />} />
        <Route path="/history" element={<ExtractionHistory />} />
        <Route path="/parts" element={<PartsView />} />
        <Route path="/bom" element={<BOMView />} />
        <Route path="/reconciliation" element={<ReconciliationView />} />
        <Route path="/estimator" element={<EstimatorView />} />
        <Route path="/estimator/finalised" element={<Navigate to="/estimator?view=finalised" replace />} />
        <Route path="/phase2-designs" element={<Navigate to="/estimator" replace />} />
        <Route path="/admin" element={<AdminView />} />
        <Route path="/teams" element={<TeamsView />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Routes>
    </>
  );
};

// Inner app component that uses the theme from context
const AppContent: React.FC = () => {
  const { theme } = useAppTheme();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BOMProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/estimator/schematic"
              element={(
                <ProtectedRoute>
                  <SchematicPopout />
                </ProtectedRoute>
              )}
            />
            
            {/* Protected routes - uses keep-alive pattern for TrainingView */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
                    <Layout>
                      <MainContent />
                    </Layout>
                  </Box>
                </ProtectedRoute>
              }
            />
          </Routes>
          <LoadingOverlay />
          <ToastContainer />
        </Router>
      </BOMProvider>
    </ThemeProvider>
  );
};

// Main App component - AuthProvider must wrap ThemeContextProvider since Theme needs useAuth
const App: React.FC = () => {
  return (
    <AuthProvider>
      <ThemeContextProvider>
        <AppContent />
      </ThemeContextProvider>
    </AuthProvider>
  );
};

export default App;

